import Accordion from './Accordion';
import { ComboBox } from 'src/components/ui/combobox';
import { IUser } from 'src/utils/interfaces/user';
import { Permissions } from 'src/utils/enums/permissions.enum';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    DealerAssociation,
    DealerPermission,
} from 'src/services/models/dealer-management.service';
import React, { Dispatch, SetStateAction } from 'react';

import './styles.scss';

enum ECountries {
    US = 'US',
    CA = 'CA',
}
interface IDealershipProps {
    selectedDealerships: Array<string>;
    onSelectDealerships: Dispatch<SetStateAction<Array<string>>>;
    permissionsMap: Map<string, Array<string>>;
    setPermissionsMap: Dispatch<SetStateAction<Map<string, Array<string>>>>;
    dealerPermissionsFromToken: Array<DealerPermission>;
    dealerAssociationsFromToken: Array<DealerAssociation>;
    initialDealerships?: Array<string>;
    disabled?: boolean;
    isDSMSearch?: boolean;
}

const Dealership: React.FC<IDealershipProps> = ({
    selectedDealerships,
    onSelectDealerships,
    permissionsMap,
    setPermissionsMap,
    dealerPermissionsFromToken,
    dealerAssociationsFromToken,
    initialDealerships,
    disabled = false,
    isDSMSearch = false,
}) => {
    const { dealerGroupState } = useSelector((state: IUser) => state);
    const emptyArraySize = 0;
    const { addNewUser } = useLocalization();
    const dealerAdmins = getDealerAdmin(
        dealerPermissionsFromToken,
        dealerAssociationsFromToken,
        initialDealerships,
        isDSMSearch,
        isDSMSearch ? dealerGroupState : null,
    );

    const selectedOptions =
        selectedDealerships && selectedDealerships.length > emptyArraySize
            ? `(${selectedDealerships.length.toString()})`
            : '';
    const onSelect = (e: any) => {
        if (selectedDealerships.includes(e)) {
            onSelectDealerships(selectedDealerships.filter((a) => a !== e));
            permissionsMap.delete(e);
            setPermissionsMap(new Map(permissionsMap));
        } else {
            permissionsMap.set(e, []);
            setPermissionsMap(new Map(permissionsMap));
            onSelectDealerships([...selectedDealerships, e]);
        }
    };

    const dealerFSPPermissions = (dealer: string) => {
        return dealerPermissionsFromToken
            .map((permission) => {
                if (permission.dealerId === dealer) {
                    return permission.permission;
                }
            })
            .filter((v): v is string => !!v);
    };

    const dealerFSPPermissionsMath = (
        dealerId: string,
    ): {
        status: string;
        perms: Permissions[] | [];
        country: string;
        manufacturerCode: string | number;
    } => {
        const dealer = dealerGroupState
            ?.flatMap((group: any) => group?.dealers ?? [])
            ?.find((item: any) => item?.dealerNumber === dealerId);

        const { country, status, manufacturerCode } = dealer;

        let perms;

        if (!dealer?.fspPermissions?.length) {
            perms = [];
        }

        const backendSet = new Set();

        dealer.fspPermissions.forEach((ele: string) => {
            if (country === ECountries.US) {
                if (ele === Permissions.SERVICE_LOANER) {
                    backendSet.add(Permissions.SERVICE_LOANER);
                    return;
                }
                if (ele === Permissions.DEALER_RENTAL) {
                    backendSet.add(Permissions.DEALER_RENTAL);
                    return;
                }
            } else if (country === ECountries.CA) {
                if (ele === Permissions.DEALER_ELECTRONIC_PAYMENT) {
                    backendSet.add(Permissions.DEALER_ELECTRONIC_PAYMENT);
                    return;
                }
            }

            backendSet.add(ele);
        });

        if (
            dealer?.country === ECountries.US &&
            dealer?.externalPermissions?.length
        ) {
            dealer.externalPermissions.forEach(
                (ele: {
                    permissionName: string;
                    permissionTypeName: string;
                }) => {
                    const hasPermissionName = Boolean(
                        ele.permissionName?.length,
                    );
                    const hasFspPermission = Boolean(
                        ele.permissionTypeName?.length,
                    );

                    const isQuickFundPermission =
                        ele.permissionName?.includes('Quick Fund') &&
                        (ele.permissionTypeName?.includes(
                            Permissions.QUICK_FUND_QF,
                        ) ||
                            ele.permissionTypeName?.includes(
                                Permissions.QUICK_FUND_NQF,
                            ));

                    if (
                        hasPermissionName &&
                        hasFspPermission &&
                        isQuickFundPermission
                    ) {
                        backendSet.add(Permissions.QUICK_FUND);
                    }
                },
            );
        }

        perms = Object.values(Permissions).filter((permission) =>
            backendSet.has(permission),
        ) as Permissions[];

        return { status, perms, country, manufacturerCode };
    };

    const dealerPermissions = selectedDealerships.map((dealer) => {
        if (isDSMSearch) {
            const { status, perms, country, manufacturerCode } =
                dealerFSPPermissionsMath(dealer);
            return (
                <Accordion
                    dealerId={dealer}
                    key={dealer}
                    permissionsMap={permissionsMap}
                    setPermissionsMap={setPermissionsMap}
                    disabled={disabled}
                    permissionsList={perms}
                    newPermissionList={[]}
                    isDSMSearch={true}
                    status={status}
                    country={country}
                    manufacturerCode={manufacturerCode}
                />
            );
        }
        return (
            <Accordion
                dealerId={dealer}
                key={dealer}
                permissionsMap={permissionsMap}
                setPermissionsMap={setPermissionsMap}
                disabled={disabled}
                permissionsList={dealerFSPPermissions(dealer)}
                isDSMSearch={isDSMSearch}
            />
        );
    });
    return (
        <div
            className={
                'new-user-container__body-dealership' +
                (disabled ? ' disabled' : '')
            }
        >
            <h3 className={'new-user-container__body-dealership-header'}>
                {addNewUser.DEALERSHIP_HEADER}
            </h3>
            <p className={'new-user-container__body-dealership-required'}>
                {addNewUser.DEALERSHIP_REQUIRED}
            </p>
            <ComboBox
                name={'dealershipSelect'}
                options={dealerAdmins}
                placeholder={addNewUser.DEALERSHIP_SELECT_PLACEHOLDER}
                defaultValue={''}
                onSelect={onSelect}
                selectedOption={selectedOptions}
                optionModifier={addNewUser.DEALERSHIP_SELECT_MODIFIER}
                multiselectable={true}
                selectedOptions={selectedDealerships}
                disabled={disabled}
            />
            {dealerPermissions}
        </div>
    );
};

function getDealerAdmin(
    dealerPermissionsFromToken: Array<DealerPermission>,
    dealerAssociationsFromToken: Array<DealerAssociation>,
    initialDealerships?: Array<string>,
    isDSMSearch?: boolean,
    dealerGroupState?: any,
) {
    const [userPermissions, associations] = [
        dealerPermissionsFromToken,
        dealerAssociationsFromToken,
    ];
    let dealerships: Array<string> = [];
    const associationsSet = new Set();

    associations.forEach((e: any) => {
        const dealerId = e.dealerId;
        if (dealerId) {
            associationsSet.add(dealerId);
        }
    });

    if (!isDSMSearch) {
        userPermissions.forEach((e: any) => {
            const dealerId = e.dealerId;

            if (
                associationsSet.has(dealerId) &&
                e.permission == Permissions.USER_ADMIN
            ) {
                dealerships.push(dealerId);
            }
        });
    } else {
        const dealershipsSet = new Set<string>();

        userPermissions.forEach((e: any) => {
            const dealerId = e.dealerId;

            if (associationsSet.has(dealerId)) {
                dealershipsSet.add(dealerId);
            }
        });

        dealerships = Array.from(dealershipsSet);
    }

    if (initialDealerships) {
        dealerships = [];
        initialDealerships.forEach((dealerId: string) => {
            if (!dealerships.includes(dealerId)) {
                dealerships.push(dealerId);
            }
        });
        if (isDSMSearch) {
            dealerships =
                dealerGroupState
                    ?.flatMap((group: { dealers: any }) => group?.dealers ?? [])
                    .map(
                        (dealer: { dealerNumber: any }) => dealer?.dealerNumber,
                    )
                    .filter((dealerNumber: any): dealerNumber is string =>
                        Boolean(dealerNumber),
                    ) ?? [];
        }
    }

    return dealerships;
}

export default Dealership;
